import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  inject,
} from '@angular/core';
import { FormControlStatus, UntypedFormControl } from '@angular/forms';
import { GoogleMap, GoogleMapsModule } from '@angular/google-maps';

import { Subscription } from 'rxjs';
import assert from 'tiny-invariant';

import { FieldType, FieldTypeConfig } from '@ngx-formly/core';
import { LatLngLiteral } from '@ts/address/shared/util-core';
import { FocusableMixin } from '@ts/shared/form/focus/util-core';
import { AutofocusIonicFieldInputProps } from '@ts/shared/form/focus/util-ionic';
import { InputFieldComponent } from '@ts/shared/form/ui-formly';
import { SharedFormWrapperUiIonFormFieldModule } from '@ts/shared/form/wrapper/ui-ion-form-field';

import { AddressFieldModel } from '../address.field.model';

/**
 * We do not set the map location to the user's current location
 * because the location provided by the google maps API is not accurate.
 * That is because the google maps API returns the location
 * of the user's providers instead of the actual device location.
 */
@Component({
  selector: 'ts-address-ui-field',
  styleUrls: ['./address-ui.field.component.scss'],
  templateUrl: './address-ui.field.component.html',
  imports: [
    CommonModule,
    GoogleMapsModule,
    SharedFormWrapperUiIonFormFieldModule,
    InputFieldComponent,
  ],
})
export class AddressUiFieldComponent
  implements AfterViewInit, OnDestroy, OnInit, FocusableMixin
{
  private changeDetectorRef = inject(ChangeDetectorRef);

  /**
   * Initial address, if any
   */
  @Input() addressFieldModelInitial?: AddressFieldModel;

  /**
   * Template options from formly
   */
  @Input({ required: true }) props!: FieldType<FieldTypeConfig>['props'];

  /**
   * Field from formly
   */
  @Input({ required: true }) field!: FieldType<FieldTypeConfig>['field'];

  /**
   * Emitted whenever the user input a new address.
   *
   * Emits null if the user inputs an invalid address.
   */
  @Output() placeSelected =
    new EventEmitter<google.maps.places.PlaceResult | null>();

  @ViewChild('mapSearchField', { static: false })
  searchField!: InputFieldComponent;
  @ViewChild(GoogleMap, { static: false }) map!: GoogleMap;

  placeholder = 'Jl. Asia Afrika...';

  googleMapOptions: google.maps.MapOptions = {
    gestureHandling: 'none',
  };

  formControl!: FieldType<FieldTypeConfig>['formControl'];
  showError = false;

  autoCompleteOptions: google.maps.places.AutocompleteOptions = {
    componentRestrictions: {
      country: 'id',
    },
    fields: ['formatted_address', 'geometry', 'place_id'],
  };
  markerPosition?: google.maps.LatLngLiteral;
  markerOptions: google.maps.MarkerOptions = {};

  /**
   * Where to pan map by default if no initial input.
   */
  mapPanDefault: LatLngLiteral = {
    lat: -6.877,
    lng: 107.618,
  };

  inputFieldProps!: AutofocusIonicFieldInputProps;

  subscriptions: Subscription[] = [];

  ngOnInit() {
    // create a new form control for the address field based on the address field of the
    // real form control
    const formControlOriginal = this.field.formControl;
    this.formControl = new UntypedFormControl(
      formControlOriginal.value?.address || '',
    );

    // copy errors from the original form control to here.
    this.subscriptions.push(
      formControlOriginal.statusChanges.subscribe((formControlStatus) => {
        (this.formControl as { status: FormControlStatus }).status =
          formControlStatus;
        this.showError = this.formControl.invalid;
        // no need to copy the error, since we pass the actual field to the validation
        // message ui
      }),
    );

    // copy value from the original form control to here.
    this.subscriptions.push(
      formControlOriginal.valueChanges.subscribe(
        (valueNew: AddressFieldModel) => {
          if (valueNew) {
            this.formControl.setValue(valueNew.address);
          }
          this.changeDetectorRef.detectChanges();
        },
      ),
    );

    // We need to initialize the map marker here because otherwise it won't be detected
    // by change detection.
    if (this.addressFieldModelInitial) {
      this.markerPosition = this.addressFieldModelInitial.geolocation;
    }

    this.inputFieldProps = {
      ...this.props,
      placeholder: this.placeholder,
      type: 'text',
    };
  }

  ngAfterViewInit() {
    this.searchField
      .getIonInput()
      .getInputElement()
      .then((inputElement) => {
        // turn on autocomplete for the input element.
        const autoComplete = new google.maps.places.Autocomplete(
          inputElement,
          this.autoCompleteOptions,
        );

        // bias searchbox towards current map's viewport.
        this.subscriptions.push(
          this.map.boundsChanged.subscribe(() => {
            const bounds = this.map.getBounds();
            if (bounds) {
              autoComplete.setBounds(bounds);
            }
          }),
        );

        // react whenever the user change their location.
        autoComplete.addListener('place_changed', () => {
          const place = autoComplete.getPlace();
          if (!place.geometry || !place.geometry.location) {
            // user entered an unrecognized place
            this.placeSelected.emit(null);
          } else {
            this.placeSelected.emit(place);
            this.refreshPlace(place);
          }
        });

        if (this.addressFieldModelInitial) {
          this.refreshPlace({
            geometry: {
              location: new google.maps.LatLng(
                this.addressFieldModelInitial.geolocation,
              ),
            },
          });
        } else {
          // otherwise, default to default location.
          this.map.panTo(this.mapPanDefault);
        }
      });
  }

  ngOnDestroy() {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }

  setFocus() {
    this.searchField.setFocus();
  }

  /**
   * Triggered when the input lose focus.
   */
  inputBlur() {
    if (this.formControl.value !== this.field.formControl.value?.address) {
      this.placeSelected.emit(null);
    }
  }

  private refreshPlace(place: google.maps.places.PlaceResult) {
    assert(place.geometry);
    assert(place.geometry.location);

    this.map.panTo(place.geometry.location);

    this.markerPosition = {
      lat: place.geometry.location.lat(),
      lng: place.geometry.location.lng(),
    };
  }

  getClass() {
    return this.field.formControl.value ? '' : 'google-map--hidden';
  }
}
